import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";

import { flatRoles, levelByName, type Ratecard, type RatecardRole, type RoleLevel } from "@/calc";
import { useEstimates } from "@/braining/store";
import { int, parseInt10, pct, rub } from "@/braining/format";
import type { CalcSettings } from "@/braining/policy-storage";
import { UNITS } from "@/braining/units";
import { roleNameTaken } from "@/braining/rates";
import { NewMark } from "@/braining/components/new-mark";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/ratecard")({ component: RatecardPage });

/**
 * Рейткарта — прайс агентства.
 *
 * Правит любой, у кого открыт раздел: Braining маленькие, и согласование
 * правки руководителем они не просили.
 *
 * Главное свойство: правка не трогает уже собранные калькуляторы. Ставки в
 * них лежат снимком, снятым в момент, когда блок попал в смету, — поэтому
 * старые сметы читаются без рейткарты и не переписываются задним числом.
 * Новые калькуляторы идут по новой цене.
 */
function RatecardPage() {
  const { ratecard, updateRatecard, library, ready } = useEstimates();
  const [pendingDelete, setPendingDelete] = useState<RatecardRole | null>(null);
  const [dragRole, setDragRole] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const roles = ratecard.groups.reduce((s, g) => s + g.roles.length, 0);

  /**
   * Роли, на которые ссылается справочник блоков.
   *
   * Связь идёт по названию — тот же VLOOKUP, что в их книгах. Поэтому
   * переименование такой роли молча оторвало бы её от блоков, и добавленный
   * блок пришёл бы без ставки. Имя у них не правится, и экран говорит почему.
   */
  const usedInLibrary = new Set(
    library.blocks.flatMap((b) => b.lines.map((l) => l.name.trim().toLowerCase())),
  );

  async function save(next: Ratecard, failure: string) {
    try {
      await updateRatecard(next);
    } catch {
      toast.error(failure);
    }
  }

  function patchRole(roleId: string, fn: (r: RatecardRole) => RatecardRole): Ratecard {
    return {
      ...ratecard,
      groups: ratecard.groups.map((g) => ({
        ...g,
        roles: g.roles.map((r) => (r.id === roleId ? fn(r) : r)),
      })),
    };
  }

  const setRate = (roleId: string, rate: number) =>
    save(
      // Дату правки пишем здесь, а не в поле ввода: она про ставку, и ставить
      // её должен тот, кто ставку меняет.
      patchRole(roleId, (r) => ({ ...r, rate, rateUpdatedAt: new Date().toISOString() })),
      "Не удалось сохранить ставку",
    );

  const setUnit = (roleId: string, unit: string) =>
    save(
      patchRole(roleId, (r) => ({ ...r, unit })),
      "Не удалось сохранить единицу",
    );

  /**
   * Имя роли — ключ, по которому её находит расчёт.
   *
   * Ставка ищется по названию (`buildRateIndex`), и два одинаковых имени
   * означают, что в индексе побеждает последнее: переименуй «Фасилитатора» в
   * «Стратега» — и десять блоков зерна начнут считать стратега по 6 000
   * вместо 7 800, молча и задним числом. У групп такая проверка была с
   * самого начала, у ролей её не было.
   */
  const setName = (roleId: string, name: string) => {
    if (roleNameTaken(ratecard, roleId, name)) {
      toast.error(`«${name.trim()}» в прайсе уже есть`, {
        description: "Ставка ищется по названию, и одинаковые имена считались бы одной ролью.",
      });
      return;
    }
    save(
      patchRole(roleId, (r) => ({ ...r, name })),
      "Не удалось сохранить название",
    );
  };

  /**
   * Уровень роли: директор или специалист.
   *
   * Нужен ассистенту — «директоров максимум треть от специалистов» иначе не
   * посчитать. Пустое значение не пропуск: тогда уровень определяется по
   * названию, и для шести ролей со словом «директор» этого достаточно. Правят
   * здесь то, где разбор по названию промахивается: «модератор» звучит как
   * специалист, а ведёт сессию за директорские деньги.
   */
  const setLevel = (roleId: string, level: RoleLevel | undefined) =>
    save(
      patchRole(roleId, ({ level: _l, ...rest }) => (level ? { ...rest, level } : rest)),
      "Не удалось сохранить уровень",
    );

  /**
   * Снять пометку «новое».
   *
   * Пометка нужна ровно один раз — показать, что роль приехала из смет, а не
   * была здесь всегда. Клик по ней и есть «увидел»: отдельная кнопка
   * «прочитано» была бы лишним шагом.
   */
  const clearNew = (roleId: string) =>
    save(
      patchRole(roleId, ({ newAt: _n, ...rest }) => rest),
      "Не удалось снять пометку",
    );

  const setDescription = (roleId: string, description: string) =>
    save(
      patchRole(roleId, (r) => ({ ...r, description })),
      "Не удалось сохранить описание",
    );

  function addRole(groupName: string) {
    // Пустое имя у новой роли — нормально: её сразу правят. А вот второй
    // пустой строки быть не должно, иначе первая же потеряется в индексе.
    if (flatRoles(ratecard).some((r) => !r.name.trim())) {
      toast.message("Сначала назовите добавленного специалиста");
      return;
    }
    const id = `role-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
    // `rateUpdatedAt` не ставим: ставки ещё нет, а дата означала бы, что её
    // сегодня проверяли. Появится вместе с первой ставкой.
    const fresh: RatecardRole = {
      id,
      name: "",
      description: "",
      unit: "час",
      rate: 0,
    };
    void save(
      {
        ...ratecard,
        groups: ratecard.groups.map((g) =>
          g.name === groupName ? { ...g, roles: [...g.roles, fresh] } : g,
        ),
      },
      "Не удалось добавить специалиста",
    );
  }

  /**
   * Перенос специалиста в другую группу.
   *
   * Группировка — это способ читать прайс, и на расчёт она не влияет: ставка
   * ищется по названию роли, а собранные калькуляторы держат её снимком.
   * Поэтому переносить можно свободно, ничего не ломая.
   */
  function moveRole(roleId: string | null, toGroup: string) {
    setDragRole(null);
    setDragOver(null);
    if (!roleId) return;

    const from = ratecard.groups.find((g) => g.roles.some((r) => r.id === roleId));
    const role = from?.roles.find((r) => r.id === roleId);
    if (!role || from?.name === toGroup) return;

    void save(
      {
        ...ratecard,
        groups: ratecard.groups.map((g) => {
          if (g.name === from?.name) return { ...g, roles: g.roles.filter((r) => r.id !== roleId) };
          if (g.name === toGroup) return { ...g, roles: [...g.roles, role] };
          return g;
        }),
      },
      "Не удалось перенести специалиста",
    );
  }

  function addGroup() {
    // Имя должно быть уникальным: по нему группа и опознаётся при переносе.
    let name = "Новая группа";
    for (let i = 2; ratecard.groups.some((g) => g.name === name); i += 1) {
      name = `Новая группа ${i}`;
    }
    void save(
      { ...ratecard, groups: [...ratecard.groups, { name, roles: [] }] },
      "Не удалось добавить группу",
    );
  }

  function renameGroup(from: string, to: string) {
    const name = to.trim();
    if (!name || ratecard.groups.some((g) => g.name === name)) return;
    void save(
      { ...ratecard, groups: ratecard.groups.map((g) => (g.name === from ? { ...g, name } : g)) },
      "Не удалось переименовать группу",
    );
  }

  /** Убирать разрешаем только пустую: вместе с ролями это была бы потеря прайса. */
  function removeGroup(name: string) {
    void save(
      { ...ratecard, groups: ratecard.groups.filter((g) => g.name !== name || g.roles.length > 0) },
      "Не удалось убрать группу",
    );
  }

  function removeRole(role: RatecardRole) {
    void save(
      {
        ...ratecard,
        groups: ratecard.groups.map((g) => ({
          ...g,
          roles: g.roles.filter((r) => r.id !== role.id),
        })),
      },
      "Не удалось убрать специалиста",
    );
    setPendingDelete(null);
  }

  return (
    <div className="space-y-5">
      <DeleteRoleDialog
        role={pendingDelete}
        usedInLibrary={
          pendingDelete ? usedInLibrary.has(pendingDelete.name.trim().toLowerCase()) : false
        }
        onCancel={() => setPendingDelete(null)}
        onConfirm={removeRole}
      />

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Рейткарта</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {roles} позиций в {ratecard.groups.length} группах, версия {ratecard.version}. Ставка и
          единица правятся на месте, специалиста можно добавить. Уже собранные калькуляторы от этого
          не меняются — в них ставка записана; по новой цене пойдут новые.
        </p>
      </div>

      <MarkupAndTaxes />

      <div className="grid gap-4">
        {ratecard.groups.map((group) => (
          <Card
            key={group.name}
            className={cn(
              "transition-colors",
              dragOver === group.name && "border-primary bg-primary/5",
            )}
            onDragOver={(e) => {
              if (!dragRole) return;
              // Без preventDefault браузер считает область запретной и не даёт
              // бросить: курсор так и остаётся перечёркнутым.
              e.preventDefault();
              setDragOver(group.name);
            }}
            onDragLeave={() => setDragOver((g) => (g === group.name ? null : g))}
            onDrop={(e) => {
              e.preventDefault();
              moveRole(dragRole, group.name);
            }}
          >
            <CardContent className="p-0">
              <div className="flex items-center gap-3 border-b bg-muted/50 px-4 py-2.5">
                <GroupTitle
                  name={group.name}
                  disabled={!ready}
                  onRename={(v) => renameGroup(group.name, v)}
                />
                {group.roles.length === 0 && (
                  <button
                    type="button"
                    onClick={() => removeGroup(group.name)}
                    disabled={!ready}
                    className="rounded p-1 text-muted-foreground/60 hover:text-destructive"
                    title="Убрать пустую группу"
                    aria-label={`Убрать группу ${group.name}`}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>

              <table className="w-full text-sm">
                <tbody>
                  {group.roles.map((role) => (
                    <RoleRow
                      key={role.id}
                      role={role}
                      disabled={!ready}
                      nameLocked={usedInLibrary.has(role.name.trim().toLowerCase())}
                      onClearNew={() => clearNew(role.id)}
                      dragging={dragRole === role.id}
                      onDragStart={() => setDragRole(role.id)}
                      onDragEnd={() => {
                        setDragRole(null);
                        setDragOver(null);
                      }}
                      onName={setName}
                      onDescription={setDescription}
                      onUnit={setUnit}
                      onLevel={setLevel}
                      onRate={setRate}
                      onRemove={() => setPendingDelete(role)}
                    />
                  ))}
                </tbody>
              </table>

              {/* Кнопка под списком, а не в шапке: добавление продолжает
                  перечень, и место новой строки видно там, где она появится. */}
              <div className="border-t px-2 py-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  disabled={!ready}
                  onClick={() => addRole(group.name)}
                >
                  <Plus className="size-3.5" />
                  Специалист
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button variant="outline" disabled={!ready} onClick={addGroup}>
        <Plus className="size-4" />
        Группа
      </Button>
    </div>
  );
}

/** Название группы правится на месте: со сметами оно не связано, ломать нечего. */
/**
 * Наценки на внешних исполнителей и ставки налогов на выплату.
 *
 * Стоит на «Рейткарте», потому что это тот же разговор про деньги за чужой
 * труд: сколько платим и сколько накидываем, перепродавая. Правит тот же
 * человек в том же ритме.
 *
 * Две карточки в одной, потому что величины разной природы. Наценки —
 * решение агентства, и цифры в коде это медианы их же архива, то есть наша
 * гипотеза. Ставки налогов агентство не выбирает, их назначает государство,
 * но менять их всё равно нужно без релиза: НДС уже уезжал с 20% на 22%.
 *
 * Наценка показывается вместе с рентабельностью, которую даёт, — это две
 * разные цифры, и путать их дорого: наценка 40% значит рентабельность 29%.
 */
function MarkupAndTaxes() {
  const { settings, updateSettings, ready } = useEstimates();
  const markup = settings.markup;

  function save(next: Partial<Omit<CalcSettings, "version">>, failure: string) {
    void updateSettings({
      planMargin: settings.planMargin,
      markup: settings.markup,
      taxRates: settings.taxRates,
      ...next,
    }).catch(() => toast.error(failure));
  }

  const setMarkup = (unit: string | null, rate: number | undefined) => {
    // Пустое поле у единицы значит «идём по общей наценке», а не «ноль».
    if (unit === null) {
      save({ markup: { ...markup, default: rate ?? 0 } }, "Не удалось сохранить наценку");
      return;
    }
    const byUnit = { ...markup.byUnit };
    if (rate === undefined) delete byUnit[unit];
    else byUnit[unit] = rate;
    save({ markup: { ...markup, byUnit } }, "Не удалось сохранить наценку");
  };

  const setTaxRate = (index: number, rate: number | undefined) => {
    const taxRates = settings.taxRates.map((t, i) => (i === index ? { ...t, rate: rate ?? 0 } : t));
    save({ taxRates }, "Не удалось сохранить ставку налога");
  };

  // Единицы, по которым имеет смысл держать отдельную наценку: те, что реально
  // встречаются в архиве. Остальные идут по общей.
  const units = ["час", "шт", "комплекс"];

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="w-44 shrink-0 text-sm font-medium">Наценка на внешних</span>
            {units.map((unit) => (
              <RateField
                key={unit}
                label={unit}
                suffix="%"
                disabled={!ready}
                value={markup.byUnit[unit]}
                placeholder={markup.default}
                hint={(v) => `рентабельность ${pct(v / (1 + v), 0)}`}
                onCommit={(v) => setMarkup(unit, v)}
              />
            ))}
            <RateField
              label="остальное"
              suffix="%"
              disabled={!ready}
              value={markup.default}
              hint={(v) => `рентабельность ${pct(v / (1 + v), 0)}`}
              onCommit={(v) => setMarkup(null, v)}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Цена клиенту = расход × (1 + наценка). Подставляется, когда строку отдают подрядчику, и
            правится в самой строке. Наценка 54% даёт рентабельность 35% — это разные цифры.
          </p>
        </div>

        <div className="border-t pt-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="w-44 shrink-0 text-sm font-medium">Налог на выплату</span>
            {settings.taxRates.map((tax, i) => (
              <RateField
                key={tax.label}
                label={tax.label}
                suffix="%"
                disabled={!ready}
                value={tax.rate}
                onCommit={(v) => setTaxRate(i, v)}
              />
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Подсказки для колонки «Налог» в расчёте. Налог идёт сверху того, о чём договорились с
            исполнителем: 80 000 на руки при 6% обходятся в 84 800.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Поле процента с подписью и пояснением к введённому.
 *
 * Пустое значение — «своей цифры нет»: тогда работает то, что в placeholder.
 * Ноль пустым не считается, он осмысленный: у нала и ИП налога правда нет.
 */
function RateField({
  label,
  value,
  placeholder,
  suffix,
  disabled,
  hint,
  onCommit,
}: {
  label: string;
  value: number | undefined;
  placeholder?: number;
  suffix: string;
  disabled?: boolean;
  hint?: (value: number) => string;
  onCommit: (value: number | undefined) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (typeof value === "number" ? String(Math.round(value * 100)) : "");

  function commit() {
    if (draft === null) return;
    const raw = draft.trim();
    setDraft(null);
    const next = raw === "" ? undefined : parseInt10(raw) / 100;
    if (next !== value) onCommit(next);
  }

  return (
    <label
      className="flex items-center gap-1.5 text-sm"
      title={hint && typeof value === "number" ? hint(value) : undefined}
    >
      <span className="text-muted-foreground">{label}</span>
      <Input
        inputMode="numeric"
        disabled={disabled}
        placeholder={typeof placeholder === "number" ? String(Math.round(placeholder * 100)) : "—"}
        className="h-8 w-16 text-right tabular-nums"
        value={shown}
        onFocus={(e) => {
          setDraft(e.currentTarget.value);
          e.currentTarget.select();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(null);
            e.currentTarget.blur();
          }
        }}
      />
      <span className="text-xs text-muted-foreground">{suffix}</span>
    </label>
  );
}

function GroupTitle({
  name,
  disabled,
  onRename,
}: {
  name: string;
  disabled?: boolean;
  onRename: (v: string) => void;
}) {
  return (
    <TextCell
      value={name}
      placeholder="название группы"
      className="text-sm font-medium"
      disabled={disabled}
      onCommit={onRename}
    />
  );
}

function RoleRow({
  role,
  disabled,
  nameLocked,
  dragging,
  onDragStart,
  onDragEnd,
  onName,
  onDescription,
  onUnit,
  onLevel,
  onRate,
  onRemove,
  onClearNew,
}: {
  role: RatecardRole;
  disabled?: boolean;
  nameLocked: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onName: (id: string, v: string) => void;
  onDescription: (id: string, v: string) => void;
  onUnit: (id: string, v: string) => void;
  onLevel: (id: string, v: RoleLevel | undefined) => void;
  onRate: (id: string, v: number) => void;
  onRemove: () => void;
  onClearNew: () => void;
}) {
  return (
    <tr className={cn("border-b align-top last:border-0", dragging && "opacity-40")}>
      {/* Тащим за ручку, а не за строку целиком: иначе выделить текст в
          названии или описании было бы нельзя — начиналось бы перетаскивание. */}
      <td className="w-7 py-2.5 pl-2">
        <span
          draggable={!disabled}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            // Safari не начинает перетаскивание, пока в dataTransfer пусто.
            e.dataTransfer.setData("text/plain", role.id);
            onDragStart();
          }}
          onDragEnd={onDragEnd}
          className="flex cursor-grab items-center justify-center py-1 text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
          title="Перетащить в другую группу"
          aria-label={`Перетащить ${role.name || "специалиста"} в другую группу`}
        >
          <GripVertical className="size-4" />
        </span>
      </td>

      <td className="w-[280px] px-4 py-2.5">
        {nameLocked ? (
          <div
            className="py-1 font-medium"
            title="Название роли связывает её со справочником блоков — переименование оторвало бы блоки от ставки"
          >
            {role.name}
          </div>
        ) : (
          <TextCell
            value={role.name}
            placeholder="название роли"
            className="font-medium"
            disabled={disabled}
            onCommit={(v) => onName(role.id, v)}
          />
        )}
        <div className="flex items-center gap-2">
          <LevelCell role={role} disabled={disabled} onCommit={onLevel} />
          {role.newAt && <NewMark onClear={onClearNew} title="Роль пришла из проверенных смет" />}
        </div>
      </td>

      <td className="px-4 py-2.5">
        <TextCell
          value={role.description}
          placeholder="что делает"
          className="text-xs leading-relaxed text-muted-foreground"
          disabled={disabled}
          onCommit={(v) => onDescription(role.id, v)}
        />
      </td>

      <td className="w-32 px-2 py-2.5">
        <UnitCell role={role} disabled={disabled} onCommit={onUnit} />
      </td>

      <td className="w-44 px-4 py-2.5 text-right">
        <RateCell role={role} disabled={disabled} onCommit={onRate} />
      </td>

      <td className="w-10 py-2.5 pr-3">
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="rounded p-1 text-muted-foreground/50 hover:text-destructive"
          aria-label={`Убрать ${role.name || "специалиста"}`}
        >
          <Trash2 className="size-3.5" />
        </button>
      </td>
    </tr>
  );
}

/** Пункт «своё» в списке единиц. Не единица, поэтому и значение служебное. */
const CUSTOM_UNIT = "__custom__";

/**
 * Единица измерения: сначала готовые варианты, своё — отдельным пунктом.
 *
 * Список закрытым быть не может — у них встречаются «интервью», а дальше
 * будут «гайдбук» и «ролик». Но и начинать с пустого поля неправильно:
 * восемь ходовых единиц покрывают почти всё, их надо показать, а не
 * заставлять вспоминать. Поэтому дропдаун, а «Своё…» — последним пунктом.
 */
function UnitCell({
  role,
  disabled,
  onCommit,
}: {
  role: RatecardRole;
  disabled?: boolean;
  onCommit: (roleId: string, unit: string) => void;
}) {
  const [typing, setTyping] = useState(false);

  if (typing) {
    return (
      <input
        autoFocus
        defaultValue={role.unit}
        placeholder="своя единица"
        className="w-full rounded border border-input bg-background px-1.5 py-1 text-xs outline-none"
        onBlur={(e) => {
          const v = e.currentTarget.value.trim();
          setTyping(false);
          if (v && v !== role.unit) onCommit(role.id, v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            // Сбрасываем значение до прежнего, иначе blur запишет набранное.
            e.currentTarget.value = role.unit;
            e.currentTarget.blur();
          }
        }}
      />
    );
  }

  // Единица, дописанная руками, показывается в списке своим пунктом: иначе
  // выбор молча подменил бы её первым попавшимся вариантом.
  const options = [...new Set<string>([...UNITS, role.unit].filter(Boolean))];

  return (
    <Select
      value={role.unit}
      disabled={disabled}
      onValueChange={(v) => (v === CUSTOM_UNIT ? setTyping(true) : onCommit(role.id, v))}
    >
      <SelectTrigger className="h-8 w-full text-xs">
        <SelectValue placeholder="единица" />
      </SelectTrigger>
      <SelectContent>
        {options.map((u) => (
          <SelectItem key={u} value={u}>
            {u}
          </SelectItem>
        ))}
        <SelectSeparator />
        <SelectItem value={CUSTOM_UNIT}>
          <span className="flex items-center gap-1.5">
            <Pencil className="size-3.5" />
            Свой вариант
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * Ставка правится на месте и коммитится по Enter или уходу из поля — как
 * ячейки в таблице расчёта. Промежуточные значения не сохраняем: набор «6000»
 * иначе проехал бы через «6», «60», «600».
 */
function RateCell({
  role,
  disabled,
  onCommit,
}: {
  role: RatecardRole;
  disabled?: boolean;
  onCommit: (roleId: string, rate: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (role.rate ? int(role.rate) : "");

  function commit() {
    if (draft === null) return;
    const value = parseInt10(draft);
    setDraft(null);
    // Ноль или пустое — это не «бесплатно», а промах: возвращаем как было.
    if (value > 0 && value !== role.rate) onCommit(role.id, value);
  }

  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="flex items-center gap-1">
        <Input
          inputMode="numeric"
          disabled={disabled}
          placeholder="ставка"
          className="h-8 w-28 text-right font-medium tabular-nums"
          value={shown}
          title={role.rate ? `Сейчас ${rub(role.rate)} за ${role.unit}` : "Ставка не задана"}
          onFocus={(e) => {
            setDraft(e.currentTarget.value);
            e.currentTarget.select();
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setDraft(null);
              e.currentTarget.blur();
            }
          }}
        />
        <span className="text-xs text-muted-foreground">₽</span>
      </span>

      {/* Дата правки — мелко и только у тронутых ставок: у ролей из исходных
          книг её нет, и «—» на весь столбец был бы шумом. */}
      {role.rateUpdatedAt && (
        <span className="pr-3 text-[10px] text-muted-foreground" title="Когда меняли ставку">
          {new Date(role.rateUpdatedAt).toLocaleDateString("ru-RU")}
        </span>
      )}
    </span>
  );
}

/** Текст, который правится на месте: коммит по Enter и уходу из поля. */
function TextCell({
  value,
  placeholder,
  className,
  list,
  disabled,
  onCommit,
}: {
  value: string;
  placeholder?: string;
  className?: string;
  /** id datalist: поле со списком-подсказкой, но со свободным вводом */
  list?: string;
  disabled?: boolean;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <input
      value={draft ?? value}
      placeholder={placeholder}
      list={list}
      disabled={disabled}
      className={`w-full rounded border border-transparent bg-transparent px-1 py-1 outline-none hover:border-input focus:border-input focus:bg-background ${className ?? ""}`}
      onFocus={(e) => setDraft(e.currentTarget.value)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null && draft.trim() !== value) onCommit(draft.trim());
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/**
 * Убрать специалиста.
 *
 * Уже собранные калькуляторы не пострадают: у них ставка записана. А вот
 * блоки справочника ссылаются на роль по названию, и если она там есть —
 * говорим об этом прямо, а не после того, как блок придёт без ставки.
 */
function DeleteRoleDialog({
  role,
  usedInLibrary,
  onCancel,
  onConfirm,
}: {
  role: RatecardRole | null;
  usedInLibrary: boolean;
  onCancel: () => void;
  onConfirm: (role: RatecardRole) => void;
}) {
  return (
    <AlertDialog open={Boolean(role)} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Убрать «{role?.name || "без названия"}» из рейткарты?</AlertDialogTitle>
          <AlertDialogDescription>
            Собранные калькуляторы не изменятся — в них ставка записана.
            {usedInLibrary
              ? " Но эта роль есть в блоках справочника: если такой блок добавят в смету, строка придёт без ставки и попросит её ввести."
              : " В блоках справочника эта роль не встречается."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          <Button variant="destructive" onClick={() => role && onConfirm(role)}>
            Убрать
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Уровень роли — мелкой строкой под названием.
 *
 * Отдельной колонки не заводим: поле нужно раз в год, а ширины в таблице и так
 * не хватает. Пока уровень не проставлен, показываем угаданный по названию и
 * говорим, что это догадка, — иначе непонятно, надо ли вмешиваться.
 */
function LevelCell({
  role,
  disabled,
  onCommit,
}: {
  role: RatecardRole;
  disabled?: boolean;
  onCommit: (roleId: string, level: RoleLevel | undefined) => void;
}) {
  const guessed = levelByName(role.name);
  const value = role.level ?? "auto";

  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(v) => onCommit(role.id, v === "auto" ? undefined : (v as RoleLevel))}
    >
      <SelectTrigger
        className="mt-1 h-6 w-fit gap-1 border-0 px-0 text-[11px] text-muted-foreground shadow-none hover:text-foreground focus:ring-0"
        title="Уровень нужен, чтобы переливать часы: «директоров максимум треть от специалистов»"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="auto">по названию · {LEVEL_LABEL[guessed].toLowerCase()}</SelectItem>
        <SelectItem value="director">{LEVEL_LABEL.director}</SelectItem>
        <SelectItem value="specialist">{LEVEL_LABEL.specialist}</SelectItem>
      </SelectContent>
    </Select>
  );
}

const LEVEL_LABEL: Record<RoleLevel, string> = {
  director: "Директор",
  specialist: "Специалист",
};
